'use client'

import React from 'react'
import { Container, Flex } from '@quark/layout'
import { Text } from '@quark/typography'
import Image from 'next/image'
import { Animations, MultiReveal, Reveal } from '@quark/animation'
import { Button } from '@quark/button'
import { NewHomePageComponent } from '@components/HomePage/utils/HomeComponentType'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { ReviewCarouselProps } from '@interfaces/home'
import isMobile from 'ismobilejs'
import AnimatedCarousel from './AnimatedCarousel'
import styles from './ReviewCarousel.module.scss'

const ReviewCarousel = (data: ReviewCarouselProps) => {
  const isMobileDevice = isMobile()?.phone

  const handleMoreStoriesButtonClick = () => {
    void (async () => {
      await trackEvent('tap_btn_interaction', {
        redirection_url: data?.buttonUrl,
        ...data?.eventProperties,
      })
      window.open(data?.buttonUrl, data?.urlInNewTab ? '_blank' : '_self')
    })()
  }

  return (
    <Container type="fluid" backgroundColor="base/primary">
      <div className={styles.sectionOverflowFix}>
        <Container
          type="fluid"
          id={NewHomePageComponent.CustomerStories}
          py={{ lg: 'spacing-72', md: 'spacing-56', sm: 'spacing-40' }}
          px={{ lg: 'spacing-80', md: 'spacing-32', sm: 'spacing-16' }}>
          <Flex
            alignItems={{ lg: 'center', md: 'center', sm: 'flex-start' }}
            gap={{ lg: 'spacing-64', md: 'spacing-48', sm: 'spacing-32' }}
            flexDirection={{ sm: 'column' }}>
            <div className={styles.reviewTitleContainer}>
              <Flex flexDirection="column" gap="spacing-48">
                <MultiReveal
                  as={Flex}
                  flexDirection="column"
                  gap={{ lg: 'spacing-16', md: 'spacing-16', sm: 'spacing-8' }}
                  animationDelays={(i: number) => i * 100}
                  animationProps={{ animation: Animations.FadeIn50px }}
                  alignItems={{ sm: 'center' }}>
                  <div className={styles.ratingsContainer}>
                    {data?.ratings.length === 1 ? (
                      <Flex alignItems="center" gap="spacing-12">
                        <Image
                          src={data?.ratings[0].image.url}
                          alt={data?.ratings[0].image.alternativeText ?? 'play-store-icon'}
                          height={40}
                          width={40}
                        />
                        <Text fontStyle="um_text-sm/normal" color="base/primary" whiteSpace="normal">
                          {data?.ratings[0].title}
                        </Text>
                      </Flex>
                    ) : (
                      <div className={styles.flipAnimation}>
                        <Flex alignItems="center" gap="spacing-12">
                          <Image
                            src={data?.ratings[0].image.url}
                            alt={data?.ratings[0].image.alternativeText ?? 'play-store-icon'}
                            height={40}
                            width={40}
                          />
                          <Text fontStyle="um_text-sm/normal" color="base/primary" whiteSpace="normal">
                            {data?.ratings[0].title}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" gap="spacing-12">
                          <Image
                            src={data?.ratings[1].image.url}
                            alt={data?.ratings[1].image.alternativeText ?? 'app-store-icon'}
                            height={40}
                            width={40}
                          />
                          <Text fontStyle="um_text-sm/normal" color="base/primary" whiteSpace="normal">
                            {data?.ratings[1].title}
                          </Text>
                        </Flex>
                      </div>
                    )}
                  </div>
                  <p>
                    <Text
                      fontStyle={{ lg: 'um_text-5xl/bold', md: 'um_text-4xl/bold', sm: 'um_text-3xl/bold' }}
                      color="base/primary">
                      <span dangerouslySetInnerHTML={{ __html: data?.title || '' }} />
                    </Text>
                  </p>
                  <p>
                    <Text
                      fontStyle={{ lg: 'um_text-base/normal', md: 'um_text-base/normal', sm: 'um_text-sm/normal' }}
                      color="base/secondary">
                      {data?.description}
                    </Text>
                  </p>
                </MultiReveal>
                {data?.buttonCta ? (
                  <Reveal
                    as="div"
                    className={styles.desktopButton}
                    animationProps={{ animation: Animations.FadeIn50px, animationDelayMs: 300 }}>
                    <Button size="lg" onClick={handleMoreStoriesButtonClick}>
                      <Text fontStyle="um_text-lg/medium" color="base/inverted primary">
                        {data?.buttonCta}
                      </Text>
                    </Button>
                  </Reveal>
                ) : null}
              </Flex>
            </div>
            <div className={styles.carouselWrapper}>
              <Reveal
                as="div"
                animationProps={{
                  animation: Animations.FadeInFromRight50px,
                }}>
                <AnimatedCarousel inpData={data?.customerStories} />
              </Reveal>
            </div>
            {isMobileDevice && data?.buttonCta ? (
              <Reveal as="div" animationProps={{ animation: Animations.FadeIn50px, animationDelayMs: 300 }}>
                <Button size="lg" onClick={handleMoreStoriesButtonClick}>
                  <Text fontStyle="um_text-lg/medium" color="base/inverted primary">
                    {data?.buttonCta}
                  </Text>
                </Button>
              </Reveal>
            ) : null}
          </Flex>
        </Container>
      </div>
    </Container>
  )
}

export default ReviewCarousel
